import React, { useCallback, useMemo, useState } from 'react';
import { CheckCircle2, Download, Info } from 'lucide-react';

import { lineHaulReassignmentBulkApi } from 'api/lineHaulReassignmentBulkApi';
import { Alert, AlertDescription } from 'shadcn/components/ui/Alert';
import { Button } from 'shadcn/components/ui/Button';
import { Card, CardContent, CardDescription, CardHeader } from 'shadcn/components/ui/Card';
import { cn } from 'shadcn/lib/utils';
import {
    BULK_UPLOAD_STATUS,
    BulkUploadDropzone,
    BulkUploadPendingChanges,
    BulkUploadRowFilterSwitch,
    BulkUploadStatus,
    BulkUploadTable,
    useBulkUpload,
} from 'shared/components/bulkUpload';

const TEMPLATE_COLUMN_DEFS = [
    { key: 'OrderId', label: 'Order ID', required: true },
    { key: 'LineHaulId', label: 'Line Haul ID', required: true },
];

const DEFAULT_COLUMN_DEFS = [
    { key: 'orderId', label: 'Order ID', required: true },
    { key: 'lineHaulId', label: 'Line Haul ID', required: true },
];

function BulkReassignLineHaul() {
    const [submittedJob, setSubmittedJob] = useState(null);

    const api = useMemo(
        () => ({
            edit: (key, params, rowsToUpdate, rowsToDelete) =>
                lineHaulReassignmentBulkApi.bulkEdit(key, params, rowsToUpdate, rowsToDelete),
            fetch: (key, params, signal) => lineHaulReassignmentBulkApi.bulkFetch(key, params, signal),
            persist: (key, params) => lineHaulReassignmentBulkApi.bulkPersist(key, params),
            upload: (file, params) => lineHaulReassignmentBulkApi.bulkUpload(file, params),
        }),
        [],
    );

    const bulkUpload = useBulkUpload({ api, params: {} });

    const handleDownloadTemplate = useCallback(() => {
        const headers = TEMPLATE_COLUMN_DEFS.map((col) => `${col.label}${col.required ? '*' : ''}`).join(',');
        const exampleValues = ['12345678', '42'].join(',');
        const csvContent = `${headers}\n${exampleValues}`;
        const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = 'bulk_reassign_line_haul_template.csv';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(url);
    }, []);

    const handleFileSelect = useCallback(
        (file) => {
            if (file) {
                bulkUpload.uploadFile(file);
            }
        },
        [bulkUpload],
    );

    const handlePersist = useCallback(async () => {
        try {
            const result = await bulkUpload.persist();
            const persistResults = result?.persistResults ?? {};
            const hasErrors = persistResults?.errors?.length > 0;
            if (!hasErrors) {
                setSubmittedJob({
                    batchId: persistResults.batchId ?? null,
                    rowCount: persistResults.successCount ?? 0,
                });
                bulkUpload.reset();
            }
        } catch {
            // Error is already captured in bulkUpload.persistError
        }
    }, [bulkUpload]);

    const handleStartNew = useCallback(() => {
        setSubmittedJob(null);
    }, []);

    const handleStartOver = useCallback(() => {
        setSubmittedJob(null);
        bulkUpload.reset();
    }, [bulkUpload]);

    const hasData = bulkUpload.rows.length > 0;
    const errorCount = bulkUpload.validationErrors.length;
    const jobSubmitted = submittedJob !== null;

    return (
        <div className="flex w-full flex-col gap-6 pb-8">
            <div className="flex flex-wrap items-start gap-3 lg:items-center">
                <h1 className="text-2xl font-semibold tracking-tight text-foreground">Bulk Reassign Line Haul</h1>
            </div>

            <div className={cn('space-y-6', hasData && 'flex min-h-[calc(100dvh-10rem)] flex-col')}>
                <Card className={cn(hasData && 'flex min-h-0 min-w-0 flex-1 flex-col')}>
                    <CardHeader>
                        <CardDescription className="leading-relaxed">
                            Upload a CSV with shop order numbers and target line haul IDs. Rows are validated before you
                            confirm reassignment.
                        </CardDescription>
                    </CardHeader>
                    <CardContent className={cn('space-y-4', hasData && 'flex min-h-0 min-w-0 flex-1 flex-col')}>
                        {!hasData && !jobSubmitted && (
                            <>
                                <Alert
                                    className="border-sky-200 bg-sky-50 text-sky-950 dark:border-sky-800 dark:bg-sky-950/40 dark:text-sky-50"
                                    role="status"
                                >
                                    <Info aria-hidden className="text-sky-600 dark:text-sky-400" />
                                    <AlertDescription className="text-sky-900 dark:text-sky-100">
                                        Order ID is the shop order number (not internal shipment ID). Each row reassigns
                                        all shipments on that order to the target line haul.
                                    </AlertDescription>
                                </Alert>
                                <div className="flex justify-end">
                                    <Button variant="outline" size="sm" onClick={handleDownloadTemplate}>
                                        <Download className="mr-2 h-4 w-4" />
                                        Download Template
                                    </Button>
                                </div>
                                <BulkUploadDropzone
                                    onFileSelect={handleFileSelect}
                                    disabled={[BULK_UPLOAD_STATUS.LOADING, BULK_UPLOAD_STATUS.UPLOADING].includes(
                                        bulkUpload.status,
                                    )}
                                    isUploading={bulkUpload.status === BULK_UPLOAD_STATUS.UPLOADING}
                                />
                            </>
                        )}

                        <BulkUploadStatus
                            entityName="Line Haul Reassignment"
                            fetchError={bulkUpload.fetchError}
                            handleStartNew={handleStartNew}
                            hasData={hasData}
                            infoMessages={[]}
                            persistError={bulkUpload.persistError}
                            persistErrors={bulkUpload.persistErrors}
                            persistSuccess={false}
                            persistingMessage="Submitting reassignments..."
                            status={bulkUpload.status}
                            uploadError={bulkUpload.uploadError}
                        />

                        {jobSubmitted && (
                            <div className="flex flex-col items-center justify-center gap-4 py-8">
                                <CheckCircle2 className="h-12 w-12 text-green-500" aria-hidden />
                                <div className="max-w-lg text-center">
                                    <p className="text-lg font-medium">Job submitted</p>
                                    <p className="text-sm text-muted-foreground">
                                        Results will be emailed to you when all reassignments finish processing.
                                        {submittedJob.rowCount > 0
                                            ? ` ${submittedJob.rowCount} order${submittedJob.rowCount === 1 ? '' : 's'} queued.`
                                            : ''}
                                    </p>
                                    {submittedJob.batchId ? (
                                        <p className="mt-3 text-sm text-muted-foreground">
                                            Reference ID:{' '}
                                            <code className="rounded bg-muted px-1.5 py-0.5 text-xs text-foreground">
                                                {submittedJob.batchId}
                                            </code>
                                        </p>
                                    ) : null}
                                </div>
                                <Button onClick={handleStartNew}>Upload Another File</Button>
                            </div>
                        )}

                        {hasData &&
                            !jobSubmitted &&
                            !bulkUpload.hasPersistErrors &&
                            ![BULK_UPLOAD_STATUS.SUBMITTING, BULK_UPLOAD_STATUS.PERSISTING].includes(
                                bulkUpload.status,
                            ) && (
                                <div className="flex min-h-0 min-w-0 flex-1 flex-col gap-4">
                                    <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                                        <div>
                                            {errorCount > 0 ? (
                                                <div className="text-sm text-destructive">
                                                    {errorCount} validation error{errorCount !== 1 ? 's' : ''} found.
                                                    Please fix errors before confirming.
                                                </div>
                                            ) : (
                                                <div className="text-sm text-green-700">
                                                    No validation errors. Ready to confirm.
                                                </div>
                                            )}
                                        </div>
                                        <div className="flex flex-wrap items-center justify-end gap-x-3 gap-y-2">
                                            <div className="flex flex-wrap items-center gap-x-3 gap-y-2">
                                                {bulkUpload.showIssuesFilterHint && (
                                                    <Alert
                                                        className="w-full max-w-md border-sky-200 bg-sky-50 py-2 pr-3 text-sm text-sky-950 sm:w-auto dark:border-sky-800 dark:bg-sky-950/40 dark:text-sky-50"
                                                        role="status"
                                                    >
                                                        <Info aria-hidden className="text-sky-600 dark:text-sky-400" />
                                                        <AlertDescription className="text-sky-900 dark:text-sky-100">
                                                            Showing only rows with validation issues, warnings, pending
                                                            edits, or rows marked for deletion. Turn off the switch to
                                                            see all rows.
                                                        </AlertDescription>
                                                    </Alert>
                                                )}
                                                {bulkUpload.showIssuesRowFilter && (
                                                    <BulkUploadRowFilterSwitch
                                                        checked={bulkUpload.showIssuesOnly}
                                                        disabled={bulkUpload.rows.length === 0}
                                                        id="bulk-reassign-line-haul-show-issues-only"
                                                        onCheckedChange={bulkUpload.setShowIssuesOnly}
                                                    />
                                                )}
                                            </div>
                                            {bulkUpload.hasUnsavedChanges && (
                                                <>
                                                    <Button
                                                        variant="outline"
                                                        size="sm"
                                                        onClick={bulkUpload.clearPendingChanges}
                                                    >
                                                        Discard Changes
                                                    </Button>
                                                    <Button
                                                        variant="default"
                                                        size="sm"
                                                        onClick={bulkUpload.submitChanges}
                                                    >
                                                        Submit Changes
                                                    </Button>
                                                </>
                                            )}
                                            <Button variant="outline" size="sm" onClick={handleStartOver}>
                                                Start Over
                                            </Button>
                                            <Button
                                                variant="default"
                                                size="sm"
                                                onClick={handlePersist}
                                                disabled={!bulkUpload.canPersist}
                                            >
                                                Confirm Reassignments
                                            </Button>
                                        </div>
                                    </div>
                                    <BulkUploadTable
                                        columnDefs={DEFAULT_COLUMN_DEFS}
                                        rows={bulkUpload.tableRows}
                                        errorsByCell={bulkUpload.errorsByCell}
                                        pendingEdits={bulkUpload.pendingEdits}
                                        updateCell={bulkUpload.updateCell}
                                        pendingDeletes={bulkUpload.pendingDeletes}
                                        toggleRowDelete={bulkUpload.toggleRowDelete}
                                    />
                                    <BulkUploadPendingChanges
                                        pendingDeletes={bulkUpload.pendingDeletes}
                                        pendingEdits={bulkUpload.pendingEdits}
                                        rowsLength={bulkUpload.rows.length}
                                    />
                                </div>
                            )}
                    </CardContent>
                </Card>
            </div>
        </div>
    );
}

export { BulkReassignLineHaul };
